import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  confirmPathwayUpload,
  downloadPathwayUploadTemplate,
  getAllBranch,
  previewPathwayUpload,
} from "../../services";
import { pushGlobalSuccess } from "../../success/globalSuccess";
import { useUserRole } from "../../utils/hooks/useUserRole";
import { Branch } from "../../types/user.type";
import { PathwayUploadPreviewData } from "../../types/pathway-upload.type";
// ตัวช่วยบันทึก blob ตัวเดียวกับที่หน้า export GB1 ใช้ — ไม่สร้างสำเนาที่สาม
import { downloadBlob } from "../Gb1SaleReport/utils";
import { checkPathwayFile } from "./fileCheck";
import { PathwayUploadFailure, resolvePreviewFailure } from "./uploadError";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PreviewReport from "./PreviewReport";

// รับเฉพาะ .xlsx/.xls ตามที่ API กำหนด (ไม่รับ .xlsm/.csv)
const ACCEPTED_TYPES =
  ".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel";

const TEMPLATE_FILENAME = "แบบฟอร์มแผนวิ่ง.xlsx";

/** สรุปกติกาของไฟล์ให้เห็นก่อนเลือกไฟล์ รายละเอียดเต็มอยู่ในชีต "วิธีใช้" ของแบบฟอร์ม */
const FORMAT_RULES = [
  'หนึ่งชีต = หนึ่งสายวิ่ง ตั้งชื่อชีตว่า "ผ.1", "ผ.2", … เรียงตามลำดับสายของสาขา ชีต ผ. ที่ว่างระบบข้ามให้',
  "ตารางบน: วันจันทร์ / วันพุธ / วันศุกร์ ตารางล่าง: วันอังคาร / วันพฤหัสบดี / วันเสาร์ (วันอาทิตย์ไม่ต้องกรอก)",
  "แต่ละวันมีคอลัมน์ ลำดับ | รหัส | ชื่อร้าน | ยอดขาย | หมายเหตุ — กรอกรหัสร้านในคอลัมน์รหัส เช่น 2095ZB0522",
  "รหัสร้านต้องยาว 10 ตัวอักษร (A-Z, 0-9) ระบบจับคู่ด้วยรหัสร้านเท่านั้น ยอดขายและหมายเหตุระบบไม่อ่าน",
  'ไฟล์แบบเดิม (ช่องเดียว "รหัสร้าน - ชื่อร้าน") ยังใช้ได้',
];

/**
 * อัปโหลดแผนวิ่งของสาขา — 2 ขั้นเสมอ
 *
 *   1. เลือกไฟล์ → ตรวจสอบ (preview)  ยังไม่มีอะไรถูกเขียนลงฐานข้อมูล
 *   2. อ่านผลตรวจ → ยืนยัน (confirm)  ระบบเขียนจากแผนที่ตรวจไว้ ไม่ได้อ่านไฟล์ซ้ำ
 *
 * ผลตรวจหมดอายุใน 30 นาที (backend เก็บไว้ชั่วคราว) หลังจากนั้นต้องอัปไฟล์ใหม่
 *
 * การตรวจไฟล์แบ่งเป็นสองชั้นโดยตั้งใจ: ฝั่งนี้ตรวจเฉพาะสิ่งที่รู้ได้ทันทีจาก
 * ไบต์ต้นไฟล์ (นามสกุล ขนาด ไฟล์ที่ถูกเปลี่ยนนามสกุลมา) ส่วน "แบบฟอร์มถูกไหม"
 * ให้ปุ่มตรวจสอบไฟล์เป็นคนตอบ เพราะนั่นคือ parser ตัวจริง — ดู fileCheck.ts
 */
export default function PathwayUpload() {
  const role = useUserRole();
  const isHo = Boolean(role.isAdmin || role.isMaster);

  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [branchUuid, setBranchUuid] = useState("");
  const [preview, setPreview] = useState<PathwayUploadPreviewData | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [failure, setFailure] = useState<PathwayUploadFailure | null>(null);
  const [checking, setChecking] = useState(false);
  const [applying, setApplying] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const onGetAllBranch = useCallback(async () => {
    if (!isHo) return;
    const {
      data: { data },
    } = await getAllBranch();
    setBranches(data);
  }, [isHo]);

  useEffect(() => {
    onGetAllBranch();
  }, [onGetAllBranch]);

  const resetPreview = () => {
    setPreview(null);
    setAcknowledged(false);
  };

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const { data } = await downloadPathwayUploadTemplate(
        isHo && branchUuid ? branchUuid : undefined,
      );
      downloadBlob(TEMPLATE_FILENAME, data);
    } catch {
      // interceptor แสดง global error ให้แล้ว
    } finally {
      setDownloading(false);
    }
  };

  const handleSelectFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] ?? null;
    // อนุญาตให้เลือกไฟล์เดิมซ้ำได้ (onChange ไม่ fire ถ้าค่าเดิม)
    event.target.value = "";
    resetPreview();
    setFailure(null);

    if (!selected) {
      setFile(null);
      return;
    }

    const problem = await checkPathwayFile(selected);
    if (problem !== null) {
      setFile(null);
      setFailure({ message: problem, showTemplateHint: true });
      return;
    }

    setFile(selected);
  };

  const handleCheck = async () => {
    if (!file) return;

    setChecking(true);
    setFailure(null);
    try {
      const { data } = await previewPathwayUpload(
        file,
        isHo ? branchUuid : undefined,
      );
      setPreview(data.data);
      setAcknowledged(false);
    } catch (error) {
      // previewPathwayUpload ปิด global toast ไว้ — แสดงเหตุผลตรงนี้แทน
      // เพื่อให้อยู่คู่กับปุ่มดาวน์โหลดแบบฟอร์ม
      resetPreview();
      setFailure(resolvePreviewFailure(error));
    } finally {
      setChecking(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview) return;

    setApplying(true);
    try {
      const { data } = await confirmPathwayUpload(
        preview.upload_id,
        acknowledged,
      );
      const applied = data.data;
      pushGlobalSuccess(
        `บันทึกแผนวิ่งสำเร็จ: ${applied.routes_applied} สาย ${applied.days_applied} วัน รวม ${applied.rows_inserted} ร้าน`,
      );
      setFile(null);
      resetPreview();
    } catch {
      // ถ้าผลตรวจหมดอายุ (404) interceptor แจ้งให้อัปไฟล์ใหม่แล้ว
    } finally {
      setApplying(false);
    }
  };

  const needsAcknowledgement = Boolean(preview && preview.summary.rows_skipped > 0);
  const canConfirm =
    Boolean(preview) && !applying && (!needsAcknowledgement || acknowledged);

  const templateButton = (
    <Button
      variant="outlined"
      color="inherit"
      size="small"
      startIcon={
        downloading ? (
          <CircularProgress size={14} color="inherit" />
        ) : (
          <DownloadIcon />
        )
      }
      onClick={handleDownloadTemplate}
      disabled={downloading}
      sx={{ whiteSpace: "nowrap" }}
    >
      ดาวน์โหลดไฟล์ตัวอย่าง
    </Button>
  );

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Typography variant="h6" mb={2}>
        อัปโหลดแผนวิ่ง
      </Typography>

      <Stack gap={2}>
        <Card sx={{ boxShadow: "none", borderRadius: "12px" }}>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Stack gap={2}>
              <Typography variant="body2" color="text.secondary">
                เลือกไฟล์แผนวิ่ง (.xlsx/.xls) ขนาดไม่เกิน 30MB —
                ระบบจะตรวจสอบและแสดงผลให้ดูก่อน ยังไม่บันทึกจนกว่าจะกดยืนยัน
              </Typography>

              <Box>
                <Typography variant="body2" fontWeight={600} mb={0.5}>
                  รูปแบบไฟล์ที่ระบบอ่านได้
                </Typography>
                <Box
                  component="ul"
                  sx={{ m: 0, pl: 2.5, color: "text.secondary" }}
                >
                  {FORMAT_RULES.map((rule) => (
                    <Typography component="li" variant="body2" key={rule}>
                      {rule}
                    </Typography>
                  ))}
                </Box>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  ยังไม่มีไฟล์? ดาวน์โหลดแบบฟอร์มเปล่าที่กรอกตามนี้ได้เลย
                  (มีชีต ผ. ครบตามจำนวนสายของสาขา และชีต &quot;วิธีใช้&quot;
                  อธิบายครบอยู่ในไฟล์)
                </Typography>
                <Box mt={1}>{templateButton}</Box>
              </Box>

              <Divider />

              {isHo && (
                <TextField
                  select
                  size="small"
                  label="สาขา"
                  value={branchUuid}
                  onChange={(event) => {
                    setBranchUuid(event.target.value);
                    resetPreview();
                  }}
                  sx={{ maxWidth: 320 }}
                >
                  {branches.map((branch) => (
                    <MenuItem key={branch.uuid} value={branch.uuid}>
                      {branch.branch_code} — {branch.branch_name}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                onChange={handleSelectFile}
                style={{ display: "none" }}
              />

              <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => inputRef.current?.click()}
                  disabled={checking || applying}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  เลือกไฟล์
                </Button>

                <Button
                  variant="contained"
                  onClick={handleCheck}
                  disabled={!file || checking || applying || (isHo && !branchUuid)}
                  startIcon={
                    checking ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {checking ? "กำลังตรวจสอบ..." : "ตรวจสอบไฟล์"}
                </Button>

                {file && (
                  <Typography variant="body2" color="text.secondary">
                    ไฟล์ที่เลือก: {file.name}
                  </Typography>
                )}
              </Stack>

              {failure && (
                <Alert
                  severity="error"
                  action={failure.showTemplateHint ? templateButton : undefined}
                >
                  <AlertTitle sx={{ mb: 0 }}>ไฟล์นี้ยังใช้ไม่ได้</AlertTitle>
                  {failure.message}
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>

        {preview && (
          <>
            <PreviewReport preview={preview} />

            <Card sx={{ boxShadow: "none", borderRadius: "12px" }}>
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Stack gap={1.5}>
                  {needsAcknowledgement && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={acknowledged}
                          onChange={(event) =>
                            setAcknowledged(event.target.checked)
                          }
                        />
                      }
                      label={`รับทราบว่ามี ${preview.summary.rows_skipped} แถวถูกข้าม`}
                    />
                  )}

                  <Alert severity="info">
                    ผลตรวจนี้หมดอายุใน&nbsp;
                    {Math.round(preview.expires_in_seconds / 60)} นาที
                    หลังจากนั้นต้องอัปโหลดไฟล์ใหม่
                  </Alert>

                  <Box>
                    <Button
                      variant="contained"
                      startIcon={
                        applying ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <CheckCircleIcon />
                        )
                      }
                      onClick={handleConfirm}
                      disabled={!canConfirm}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {applying
                        ? "กำลังบันทึก..."
                        : `ยืนยันบันทึก ${preview.summary.rows_to_apply} ร้าน`}
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </>
        )}
      </Stack>
    </Box>
  );
}
